import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { ANAF_DISCLAIMER_VERSION } from "@/lib/legalVersions";

export type AnafAckStatus = "loading" | "needs_ack" | "acked";

const queryKey = (userId: string | undefined) =>
  ["anaf-disclaimer-ack", userId, ANAF_DISCLAIMER_VERSION] as const;

/**
 * Whether the current user has acknowledged the ANAF / SPV disclaimer at the
 * current ANAF_DISCLAIMER_VERSION. Cached via React Query so navigating
 * between NewInvoice / EditInvoice / InvoiceDetail doesn't re-hit the DB.
 * Bumping the version invalidates automatically (it's part of the cache key).
 */
export function useAnafDisclaimerStatus() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKey(user?.id),
    enabled: !!user?.id,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("terms_acceptance_log")
        .select("id")
        .eq("user_id", user!.id)
        .eq("document_type", "anaf_disclaimer")
        .eq("version", ANAF_DISCLAIMER_VERSION)
        .limit(1)
        .maybeSingle();
      if (error) {
        console.warn("ANAF disclaimer status check failed:", error);
        // Fail-safe: require ack so we don't lose the legal proof.
        return false;
      }
      return !!data;
    },
  });

  const status: AnafAckStatus = !user
    ? "needs_ack"
    : isLoading
      ? "loading"
      : data
        ? "acked"
        : "needs_ack";

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: queryKey(user?.id) });

  /** Optimistic local mark as acked — used right after a successful insert. */
  const markAcked = () =>
    queryClient.setQueryData(queryKey(user?.id), true);

  return { status, refresh, markAcked };
}
